import { FC, useMemo } from 'react';
import { ContractChargeDetailDto } from 'src/generated/services/TFinancialApi';
import { beautifyEnum, formatMoney, formatNumber } from 'src/utils/utility';
import { formatDateString } from 'src/utils/date';
import { Tooltip } from '@mui/material';

export interface IChargeSelectionListProps {
  charges: ContractChargeDetailDto[];
  selectedCharge?: ContractChargeDetailDto | null;
  onChargeSelect?: (charge: ContractChargeDetailDto) => void;
  mode?: 'selection' | 'display';
  title?: string;
  showHeader?: boolean;
  maxHeight?: string;
  emptyMessage?: string;
  emptySubMessage?: string;
}

// Group charges by charge name
interface GroupedCharge {
  chargeName: string;
  charges: ContractChargeDetailDto[];
  count: number;
}

// Function to format charge details for tooltip
const formatChargeDetails = (charge: ContractChargeDetailDto): React.ReactNode => {
  const Separator = () => <span className='text-neutral-40 mx-4'>•</span>;

  switch (charge.chargeType) {
    case 'WEIGHT':
      return (
        <div className='flex items-start justify-between gap-16 py-12'>
          <div className='flex-1 min-w-0'>
            <div className='text-14 font-semibold text-white mb-2'>
              <Separator />[{charge.minimumChargeableWeight || 'N/A'} - {charge.maximumChargeableWeight || 'N/A'}]
            </div>
            {(charge.startDate || charge.endDate) && (
              <div className='text-12 font-normal text-neutral-60'>
                {formatDateString(charge.startDate)} - {formatDateString(charge.endDate)}
              </div>
            )}
          </div>
          <div className='flex items-center gap-16 flex-shrink-0'>
            <div className='text-right'>
              <div className='text-12 font-semibold text-white'>{formatMoney(charge.unitPrice, charge.currency || 'USD')}</div>
              <div className='text-12 font-normal text-neutral-60'>Price per unit</div>
            </div>
            {charge.minimumPrice !== null && charge.minimumPrice !== undefined && (
              <>
                <div className='w-px h-40 bg-white/20' />
                <div className='text-right'>
                  <div className='text-12 font-semibold text-white'>{formatMoney(charge.minimumPrice, charge.currency || 'USD')}</div>
                  <div className='text-12 font-normal text-neutral-60'>Minimum Price</div>
                </div>
              </>
            )}
          </div>
        </div>
      );

    case 'SHIPMENT':
    case 'DECLARATION':
      return (
        <div className='flex items-start justify-between gap-16 py-12'>
          <div className='flex-1 min-w-0'>
            {(charge.startDate || charge.endDate) && (
              <div className='text-12 font-normal text-neutral-60'>
                {formatDateString(charge.startDate)} - {formatDateString(charge.endDate)}
              </div>
            )}
          </div>
          <div className='text-right flex-shrink-0'>
            <div className='text-12 font-semibold text-white'>{formatMoney(charge.unitPrice, charge.currency || 'USD')}</div>
            <div className='text-12 font-normal text-neutral-60'>Unit Price</div>
          </div>
        </div>
      );

    case 'PERCENTAGE':
      return (
        <div className='flex items-start justify-between gap-16 py-12'>
          <div className='flex-1 min-w-0'>
            {(charge.startDate || charge.endDate) && (
              <div className='text-12 font-normal text-neutral-60'>
                {formatDateString(charge.startDate)} - {formatDateString(charge.endDate)}
              </div>
            )}
          </div>
          <div className='flex items-center gap-16 flex-shrink-0'>
            {charge.minimumPrice !== null && charge.minimumPrice !== undefined && (
              <>
                <div className='text-right'>
                  <div className='text-12 font-semibold text-white'>{formatMoney(charge.minimumPrice, charge.currency || 'USD')}</div>
                  <div className='text-12 font-normal text-neutral-60'>Minimum Price</div>
                </div>
                <div className='w-px h-40 bg-white/20' />
              </>
            )}
            <div className='text-right'>
              <div className='text-12 font-semibold text-white'>{formatNumber(charge.percentageValue)}%</div>
              <div className='text-12 font-normal text-neutral-60'>Percentage Value</div>
            </div>
          </div>
        </div>
      );

    case 'CONTAINER':
    default: {
      const has20ft = charge.unitPrice20 !== null && charge.unitPrice20 !== undefined;
      const has40ft = charge.unitPrice40 !== null && charge.unitPrice40 !== undefined;
      const hasBoth = has20ft && has40ft;

      return (
        <div className='flex items-start justify-between gap-16 py-12'>
          <div className='flex-1 min-w-0'>
            <div className='text-14 font-semibold text-white mb-2'>{beautifyEnum(charge.containerType || '')}</div>
            {(charge.startDate || charge.endDate) && (
              <div className='text-14 font-semibold text-white mb-2'>
                {formatDateString(charge.startDate)} - {formatDateString(charge.endDate)}
              </div>
            )}
          </div>
          <div className='flex items-center gap-16 flex-shrink-0'>
            {has20ft && (
              <div className='text-right'>
                <div className='text-12 font-semibold text-white'>{formatMoney(charge.unitPrice20, charge.currency || 'USD')}</div>
                <div className='text-12 font-normal text-neutral-60'>20ft Unit Price</div>
              </div>
            )}
            {hasBoth && <div className='w-px h-40 bg-white/20' />}
            {has40ft && (
              <div className='text-right'>
                <div className='text-12 font-semibold text-white'>{formatMoney(charge.unitPrice40, charge.currency || 'USD')}</div>
                <div className='text-12 font-normal text-neutral-60'>40ft Unit Price</div>
              </div>
            )}
          </div>
        </div>
      );
    }
  }
};

// Tooltip content component
const ChargeTooltipContent: FC<{ charges: ContractChargeDetailDto[] }> = ({ charges }) => {
  return (
    <div className='bg-[#0f172a] rounded-lg p-16 min-w-[480px] max-w-[640px]'>
      <div className={`${charges.length > 5 ? 'max-h-[320px] overflow-y-auto' : ''} divide-y divide-white/10`}>
        {charges.map((charge, index) => (
          <div key={`${charge.chargeName}-${charge.chargeType}-${index}`}>{formatChargeDetails(charge)}</div>
        ))}
      </div>
    </div>
  );
};

// Single charge item component (shows full details)
const SingleChargeItem: FC<{
  charge: ContractChargeDetailDto;
  isSelected?: boolean;
  onSelect?: (chargeName: string) => void;
  mode: 'selection' | 'display';
}> = ({ charge, isSelected = false, onSelect, mode }) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect?.(charge.chargeName || '');
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    if (mode === 'selection') {
      e.preventDefault();
      e.stopPropagation();
      onSelect?.(charge.chargeName || '');
    }
  };

  const itemClasses =
    mode === 'selection'
      ? `flex items-center p-16 border-b border-neutral-10 cursor-pointer hover:bg-neutral-5 transition-colors ${
          isSelected ? 'bg-primary-5 border-primary-20' : ''
        }`
      : 'flex items-center p-16 border-b border-neutral-10';

  // Format charge name with details
  const formatChargeName = (): React.ReactNode => {
    const Separator = () => <span className='text-neutral-20 mx-8'>•</span>;

    switch (charge.chargeType) {
      case 'WEIGHT':
        return (
          <span className='flex items-center'>
            <span>{beautifyEnum(charge.chargeName || '')}</span>
            <Separator />
            <span>
              [{charge.minimumChargeableWeight || 'N/A'} - {charge.maximumChargeableWeight || 'N/A'}]
            </span>
          </span>
        );

      case 'SHIPMENT':
      case 'DECLARATION':
      case 'PERCENTAGE':
        return (
          <span className='flex items-center'>
            <span>{beautifyEnum(charge.chargeName || '')}</span>
          </span>
        );

      case 'CONTAINER':
      default:
        return (
          <span className='flex items-center'>
            <span>{beautifyEnum(charge.chargeName || '')}</span>
            <Separator />
            <span>{beautifyEnum(charge.containerType || '')}</span>
          </span>
        );
    }
  };

  return (
    <div
      className={itemClasses}
      role={mode === 'selection' ? 'button' : undefined}
      tabIndex={mode === 'selection' ? 0 : undefined}
      aria-pressed={isSelected}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {/* Radio Button - only in selection mode */}
      {mode === 'selection' && (
        <div className='flex-shrink-0 mr-16'>
          <div
            className={`w-20 h-20 rounded-full border-2 flex items-center justify-center ${
              isSelected ? 'border-primary bg-primary' : 'border-neutral-30 bg-white'
            }`}
          >
            {isSelected && <div className='w-8 h-8 rounded-full bg-white' />}
          </div>
        </div>
      )}

      {/* Charge Info */}
      <div className='flex-1 min-w-0'>
        <div className='flex items-center justify-between gap-16'>
          <div className='flex-shrink-0'>
            <h5 className='text-14 xl:text-16 font-medium text-blue_grey-90 mb-4'>{formatChargeName()}</h5>
            {(charge.startDate || charge.endDate) && (
              <div className='flex items-center gap-16 !text-12 xl:!text-14 text-neutral-60'>
                <span>
                  {formatDateString(charge.startDate)} - {formatDateString(charge.endDate)}
                </span>
              </div>
            )}
          </div>

          {/* Pricing */}
          <div className='text-right flex-shrink-0'>
            {charge.chargeType === 'WEIGHT' && charge.unitPrice !== null && charge.unitPrice !== undefined && (
              <div className='flex items-center gap-10'>
                <div className='text-right'>
                  <div className='!text-12 xl:!text-14 font-semibold text-neutral-90'>
                    {formatMoney(charge.unitPrice, charge.currency || 'USD')} / {charge.chargeUnit || 'kg'}
                  </div>
                  <div className='!text-12 xl:!text-14 text-neutral-60'>Price per unit</div>
                </div>
                {charge.minimumPrice !== null && charge.minimumPrice !== undefined && (
                  <>
                    <div className='w-1 h-32 bg-neutral-20' />
                    <div className='text-right'>
                      <div className='!text-12 xl:!text-14 font-semibold text-neutral-90'>{formatMoney(charge.minimumPrice, charge.currency || 'USD')}</div>
                      <div className='!text-12 xl:!text-14 text-neutral-60'>Minimum Price</div>
                    </div>
                  </>
                )}
              </div>
            )}
            {(charge.chargeType === 'SHIPMENT' || charge.chargeType === 'DECLARATION') && charge.unitPrice !== null && charge.unitPrice !== undefined && (
              <>
                <div className='!text-12 xl:!text-14 font-semibold text-neutral-90'>{formatMoney(charge.unitPrice, charge.currency || 'USD')}</div>
                <div className='!text-12 xl:!text-14 text-neutral-60'>Unit Price</div>
              </>
            )}
            {charge.chargeType === 'PERCENTAGE' && charge.percentageValue !== null && charge.percentageValue !== undefined && (
              <div className='flex items-center gap-10'>
                {charge.minimumPrice !== null && charge.minimumPrice !== undefined && (
                  <div className='text-right'>
                    <div className='!text-12 xl:!text-14 font-semibold text-neutral-90'>{formatMoney(charge.minimumPrice, charge.currency || 'USD')}</div>
                    <div className='!text-12 xl:!text-14 text-neutral-60'>Minimum Price</div>
                  </div>
                )}
                {charge.minimumPrice !== null && charge.minimumPrice !== undefined && <div className='w-1 h-32 bg-neutral-20' />}
                <div className='text-right'>
                  <div className='!text-12 xl:!text-14 font-semibold text-neutral-90'>{formatNumber(charge.percentageValue)}%</div>
                  <div className='!text-12 xl:!text-14 text-neutral-60'>Percentage Value</div>
                </div>
              </div>
            )}
            {charge.chargeType === 'CONTAINER' && (
              <div className='flex items-center gap-10'>
                {charge.unitPrice20 !== null && charge.unitPrice20 !== undefined && (
                  <div className='text-right'>
                    <div className='!text-12 xl:!text-14 font-semibold text-neutral-90'>{formatMoney(charge.unitPrice20, charge.currency || 'USD')}</div>
                    <div className='!text-12 xl:!text-14 text-neutral-60'>20ft Unit Price</div>
                  </div>
                )}
                {charge.unitPrice20 !== null && charge.unitPrice20 !== undefined && charge.unitPrice40 !== null && charge.unitPrice40 !== undefined && (
                  <div className='w-1 h-32 bg-neutral-20 mx-2' />
                )}
                {charge.unitPrice40 !== null && charge.unitPrice40 !== undefined && (
                  <div className='text-right'>
                    <div className='!text-12 xl:!text-14 font-semibold text-neutral-90'>{formatMoney(charge.unitPrice40, charge.currency || 'USD')}</div>
                    <div className='!text-12 xl:!text-14 text-neutral-60'>40ft Unit Price</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Grouped charge item component (for multiple charges with same name)
const GroupedChargeItem: FC<{
  groupedCharge: GroupedCharge;
  isSelected?: boolean;
  onSelect?: (chargeName: string) => void;
  mode: 'selection' | 'display';
}> = ({ groupedCharge, isSelected = false, onSelect, mode }) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect?.(groupedCharge.chargeName);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    if (mode === 'selection') {
      e.preventDefault();
      e.stopPropagation();
      onSelect?.(groupedCharge.chargeName);
    }
  };

  const handleInfoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const itemClasses =
    mode === 'selection'
      ? `flex items-center justify-between p-16 border-b border-neutral-10 cursor-pointer hover:bg-neutral-5 transition-colors ${
          isSelected ? 'bg-primary-5 border-primary-20' : ''
        }`
      : 'flex items-center justify-between p-16 border-b border-neutral-10';

  return (
    <div
      className={itemClasses}
      role={mode === 'selection' ? 'button' : undefined}
      tabIndex={mode === 'selection' ? 0 : undefined}
      aria-pressed={isSelected}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {/* Left side: Radio button and charge name */}
      <div className='flex items-center gap-16 flex-1 min-w-0'>
        {mode === 'selection' && (
          <div className='flex-shrink-0'>
            <div
              className={`w-20 h-20 rounded-full border-2 flex items-center justify-center ${
                isSelected ? 'border-primary bg-primary' : 'border-neutral-30 bg-white'
              }`}
            >
              {isSelected && <div className='w-8 h-8 rounded-full bg-white' />}
            </div>
          </div>
        )}
        <h5 className='text-14 xl:text-16 font-medium text-blue_grey-90'>{beautifyEnum(groupedCharge.chargeName)}</h5>
      </div>

      {/* Right side: Counter and info icon */}
      <div className='flex items-center gap-12 flex-shrink-0'>
        {/* Show tooltip for multiple charges in both selection and display mode */}
        {groupedCharge.count > 1 ? (
          <>
            <span className='!text-11 xl:!text-12 text-neutral-60'>{groupedCharge.count} Charges</span>
            <Tooltip
              title={<ChargeTooltipContent charges={groupedCharge.charges} />}
              placement='left'
              arrow
              enterDelay={200}
              leaveDelay={0}
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: 'transparent',
                    padding: 0,
                    maxWidth: 'none',
                  },
                },
                arrow: {
                  sx: {
                    color: '#0f172a',
                  },
                },
              }}
            >
              <button
                type='button'
                onClick={handleInfoClick}
                className='w-20 h-20 rounded-full bg-neutral-10 hover:bg-neutral-20 flex items-center justify-center transition-colors'
                aria-label='Show charge details'
              >
                <svg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <path
                    d='M6 11C8.76142 11 11 8.76142 11 6C11 3.23858 8.76142 1 6 1C3.23858 1 1 3.23858 1 6C1 8.76142 3.23858 11 6 11Z'
                    stroke='#64748b'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <path d='M6 8V6' stroke='#64748b' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
                  <path d='M6 4H6.005' stroke='#64748b' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
                </svg>
              </button>
            </Tooltip>
          </>
        ) : null}
      </div>
    </div>
  );
};

export const ChargeSelectionList: FC<IChargeSelectionListProps> = ({
  charges,
  selectedCharge,
  onChargeSelect,
  mode = 'selection',
  title,
  showHeader = true,
  maxHeight = 'max-h-400',
  emptyMessage = 'No charges available',
  emptySubMessage = 'Please select leg type and charge domain first',
}) => {
  // Group charges by charge name
  const groupedCharges = useMemo(() => {
    const groups = new Map<string, ContractChargeDetailDto[]>();

    // In display mode, show all charges with the same name as the selected charge
    let chargesToProcess: ContractChargeDetailDto[];
    if (mode === 'display' && selectedCharge) {
      // Find all charges with the same name as the selected charge
      chargesToProcess = charges?.filter((c) => c.chargeName === selectedCharge.chargeName) || [selectedCharge];
    } else {
      chargesToProcess = charges || [];
    }

    chargesToProcess.forEach((charge) => {
      const name = charge.chargeName || '';
      if (!groups.has(name)) {
        groups.set(name, []);
      }
      groups.get(name)!.push(charge);
    });

    return Array.from(groups.entries()).map(([chargeName, chargeList]) => ({
      chargeName,
      charges: chargeList,
      count: chargeList.length,
    }));
  }, [charges, selectedCharge, mode]);

  const shouldShowHeader = mode === 'selection' ? showHeader : false;
  const totalCharges = charges?.length || 0;

  const handleGroupSelect = (chargeName: string) => {
    // Find the first charge with this name and call the original onChargeSelect
    const charge = charges?.find((c) => c.chargeName === chargeName);
    if (charge) {
      onChargeSelect?.(charge);
    }
  };

  return (
    <div className='border border-neutral-20 rounded-lg bg-white overflow-hidden'>
      {/* Header */}
      {shouldShowHeader && (
        <div className='bg-neutral-5 px-16 py-12 border-b border-neutral-20'>
          <div className='flex items-center justify-between'>
            <h4 className='text-14 xl:text-16 font-semibold text-neutral-80'>{title || (mode === 'selection' ? 'Select Charge Type' : 'Charge Details')}</h4>
            <span className='!text-11 xl:!text-12 text-neutral-60'>Showing {totalCharges} Charges</span>
          </div>
        </div>
      )}

      {/* Charge List */}
      <div className={`${maxHeight} overflow-y-auto`}>
        {groupedCharges && groupedCharges.length > 0 ? (
          groupedCharges.map((groupedCharge) => {
            const isSelected = selectedCharge?.chargeName === groupedCharge.chargeName;

            // If only one charge with this name, show full details (SingleChargeItem)
            if (groupedCharge.count === 1) {
              return (
                <SingleChargeItem
                  key={groupedCharge.chargeName}
                  charge={groupedCharge.charges[0]}
                  isSelected={isSelected}
                  onSelect={handleGroupSelect}
                  mode={mode}
                />
              );
            }

            // If multiple charges with same name, show grouped view with tooltip
            return (
              <GroupedChargeItem
                key={groupedCharge.chargeName}
                groupedCharge={groupedCharge}
                isSelected={isSelected}
                onSelect={handleGroupSelect}
                mode={mode}
              />
            );
          })
        ) : (
          <div className='p-32 text-center text-neutral-60'>
            <div className='text-14 xl:text-16'>{emptyMessage}</div>
            <div className='text-12 xl:text-14 mt-4'>{emptySubMessage}</div>
          </div>
        )}
      </div>
    </div>
  );
};
